// index.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import InterventionDetails from './components/InterventionDetails';
import { appointmentService, checklistService } from '../../services/supabaseService';
import { bunny } from '../../lib/bunnyNet';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const InterventionPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [activeAppointment, setActiveAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [checklistLoaded, setChecklistLoaded] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    const fetchAppointmentDetails = async () => {
      if (!id) {
        setError('ID d\'intervention manquant');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setChecklistLoaded(false);
        
        // Récupérer les détails de l'appointment
        const data = await appointmentService.getAppointmentById(id);
        console.log('Détails de l\'intervention récupérés:', data.data);
        
        let appointment = data.data;
        let checklists = appointment.Service?.checklists || [];
        console.log('Checklists associées au service:', checklists);
        let savedchecklist = [];
        if (appointment?.appointment_result?.[0]?.checklist) {
          savedchecklist = appointment.appointment_result[0].checklist;
        }
        console.log('Checklists associées au service:', savedchecklist);
        
        // Récupérer les données sauvegardées si elles existent
        let savedData = null;
        try {
          const savedResponse = await appointmentService.getSavedInterventionData(id);
          savedData = savedResponse?.data;
          console.log('Données sauvegardées récupérées:', savedData);
        } catch (err) {
          console.log('Aucune donnée sauvegardée trouvée');
        }
        
        // Récupérer les images sauvegardées
        let savedPhotos = { before: [], after: [] };
        if (savedData?.photos) {
          savedPhotos = savedData.photos;
        } else if (savedData?.appointment_result?.[0]?.image?.[0]) {
          savedPhotos = savedData.appointment_result[0].image[0];
        } else if (appointment?.appointment_result?.[0]?.image?.[0]) {
          savedPhotos = appointment.appointment_result[0].image[0];
        }

        // Récupérer la signature sauvegardée
        let savedSignature = null;
        if (savedData?.signature) {
          savedSignature = savedData.signature;
        } else if (savedData?.appointment_result?.[0]?.signature) {
          savedSignature = savedData.appointment_result[0].signature;
        } else if (appointment?.appointment_result?.[0]?.signature) {
          savedSignature = appointment.appointment_result[0].signature;
        }
        console.log('Signature récupérée:', savedSignature);

        // Récupérer les consommables sauvegardés (depuis la table consumable_usage)
        let savedConsumables = [];
        try {
          console.log('🔍 Récupération des consommables pour l\'intervention:', id);
          
          const { data: consumablesData, error: consumablesError } = await supabase
            ?.from('consumable_usage')
            ?.select(`
              *,
              consumable:consumable_id (*)
            `)
            ?.eq('appointment_id', id);

          if (consumablesError) {
            console.error('❌ Erreur lors de la récupération des consommables:', consumablesError);
          } else if (consumablesData && consumablesData.length > 0) {
            console.log('✅ Consommables récupérés depuis consumable_usage:', consumablesData);
            
            // Transformer les données pour le format attendu par InterventionDetails
            savedConsumables = consumablesData.map(item => ({
              id: item.consumable_id,
              name: item.consumable?.name || 'Consommable',
              unit: item.consumable?.unit || 'pièce',
              category: item.consumable?.category || 'Autres',
              quantity: item.quantity_used,
              usage_date: item.usage_date,
              notes: item.notes
            }));
          } else {
            console.log('Aucun consommable trouvé pour cette intervention');
            
            // Fallback: vérifier dans savedData ou appointment_result
            if (savedData?.consumables && savedData.consumables.length > 0) {
              console.log('📦 Consommables récupérés depuis savedData:', savedData.consumables);
              savedConsumables = savedData.consumables;
            } else if (appointment?.appointment_result?.[0]?.consumables) {
              console.log('📦 Consommables récupérés depuis appointment_result:', appointment.appointment_result[0].consumables);
              savedConsumables = appointment.appointment_result[0].consumables;
            }
          }
        } catch (error) {
          console.error('❌ Erreur lors de la récupération des consommables:', error);
        }
        
        // Initialiser l'appointment
        let baseAppointmentData = {
          reference: appointment.reference || `REF-${id}`,
          id: appointment.id || id,
          customerName: appointment.customer_name || 'Client inconnu',
          customerAvatar: appointment.customer_avatar ,
          customerAvatarAlt: `Photo de ${appointment.customer_name || 'client'}`,
          serviceType: appointment.service_type || appointment.service_name || 'Service non spécifié',
          scheduledTime: appointment.scheduled_time_start || '08:00',
          scheduleddate: appointment.scheduled_date,
          scheduled_time_start: appointment.scheduled_time_start || '08:00',
          scheduled_time_end: appointment.scheduled_time_end || '09:00',
          address: appointment.customer_address || 'Adresse non spécifiée',
          phone: appointment.customer_phone || '+33 0 00 00 00 00',
          status: appointment.status || 'pending',
          notes: savedData?.notes || appointment.notes || '',
          latitude: appointment.customer_latitude || 48.8566,
          longitude: appointment.customer_longitude || 2.3522,
          photos: savedPhotos,
          signature: savedSignature,
          consumables: savedConsumables,
          technician_id: appointment.technician_id || null,
          checklist: [],
        };
        
        let Pointcontrole = [];
        
        if (checklists && checklists.length > 0) {
          // Charger les checklists
          const checklistPromises = checklists.map(async (checklistId, index) => {
            try {
              const checklistData = await checklistService.getChecklistById(checklistId);
              if (checklistData?.data) {
                // Vérifier si cette checklist a été sauvegardée
                const savedChecklistItem = savedchecklist.find(
                  item => item.id === checklistData.data.id || item.id === checklistId
                );
                
                return {
                  id: checklistData.data.id || checklistId || index + 1,
                  label: checklistData.data.titre || `Point de contrôle ${index + 1}`,
                  checked: savedChecklistItem?.checked || false,
                  required: true
                };
              }
              return null;
            } catch (err) {
              console.error(`Erreur lors du chargement de la checklist ${checklistId}:`, err);
              return null;
            }
          });
          
          const checklistResults = await Promise.all(checklistPromises);
          Pointcontrole = checklistResults.filter(item => item !== null);
        } else {
          // Checklist par défaut
          Pointcontrole = savedData?.checklist || [];
        }
        
        const appointmentData = {
          ...baseAppointmentData,
          checklist: Pointcontrole
        };
        
        console.log('Données complètes de l\'appointment:', appointmentData);
        setActiveAppointment(appointmentData);
        setChecklistLoaded(true);
        
      } catch (err) {
        console.error('Erreur lors du chargement des détails:', err);
        setError('Impossible de charger les détails de l\'intervention.');
        setActiveAppointment(null);
      } finally {
        setLoading(false);
      }
    };

    fetchAppointmentDetails();
  }, [id]);

  /**
   * Convertit un blob URL en objet File
   */
  const blobUrlToFile = async (blobUrl, type = 'before') => {
    try {
      const response = await fetch(blobUrl);
      const blob = await response.blob();
      
      // Créer un nom de fichier basé sur le timestamp et le type
      const timestamp = Date.now();
      const extension = blob.type?.split('/')[1] || 'jpg';
      const fileName = `${type}_${timestamp}.${extension}`;
      
      return new File([blob], fileName, { type: blob.type || 'image/jpeg' });
    } catch (error) {
      console.error('❌ Erreur conversion blob URL:', error);
      throw error;
    }
  };

  /**
   * Vérifie si un item est un File ou Blob
   */
  const isFileObject = (item) => {
    return item instanceof File || item instanceof Blob;
  };

  /**
   * Vérifie si un item est un blob URL
   */
  const isBlobUrl = (item) => {
    return typeof item === 'string' && item.startsWith('blob:');
  };

  /**
   * Vérifie si un item est une URL Bunny déjà uploadée
   */
  const isBunnyUrl = (item) => {
    return typeof item === 'string' && item.includes('b-cdn.net');
  };

  /**
   * Vérifie si un item est un objet photo avec propriété url
   */
  const isPhotoObject = (item) => {
    return item && typeof item === 'object' && item.url;
  };

  /**
   * Extrait l'URL d'un item photo
   */
  const extractUrl = (item) => {
    if (typeof item === 'string') return item;
    if (item?.url) return item.url;
    return null;
  };

  /**
   * Fonction pour uploader une image vers Bunny.net
   */
  const uploadFileToBunny = async (file, type = 'before') => {
    try {
      console.log('📤 Préparation upload:', file);
      
      // Si c'est une URL Bunny, retourner directement l'URL
      if (isBunnyUrl(file)) {
        console.log('✅ Déjà une URL Bunny:', file);
        return file;
      }
      
      // Si c'est un objet photo, extraire l'URL
      if (isPhotoObject(file)) {
        if (isBunnyUrl(file.url)) {
          return file.url;
        }
        file = file.url;
      }
      
      // Si c'est un blob URL, le convertir en File
      let fileToUpload = file;
      if (isBlobUrl(file)) {
        console.log('🔄 Conversion blob URL en File');
        fileToUpload = await blobUrlToFile(file, type);
      }
      
      // Vérifier que c'est bien un objet File
      if (!isFileObject(fileToUpload)) {
        console.error('❌ Ce n\'est pas un fichier valide:', fileToUpload);
        throw new Error('Format de fichier non supporté');
      }
      
      console.log('📤 Upload fichier:', fileToUpload.name, 'type:', fileToUpload.type, 'taille:', fileToUpload.size);
      
      // Upload vers Bunny.net
      const fileUrl = await bunny.uploadImage(fileToUpload);
      console.log('✅ Upload réussi:', fileUrl);
      
      return fileUrl;
    } catch (error) {
      console.error('❌ Erreur lors de l\'upload vers Bunny.net:', error);
      throw error;
    }
  };

  const handleUpdateConsumables = (consumables) => {
    setActiveAppointment((prev) => ({
      ...prev,
      consumables: consumables
    }));
  };

  /**
   * Upload la signature vers Bunny.net
   */
  const uploadSignatureToBunny = async (signatureDataUrl) => {
    try {
      console.log('📤 Upload signature vers Bunny.net');
      
      // Convertir le dataURL en Blob/File
      const response = await fetch(signatureDataUrl);
      const blob = await response.blob();
      
      // Créer un nom de fichier pour la signature
      const timestamp = Date.now();
      const fileName = `signature_${timestamp}_${id}.png`;
      const file = new File([blob], fileName, { type: 'image/png' });
      
      // Upload vers Bunny.net
      const fileUrl = await bunny.uploadImage(file);
      console.log('✅ Signature uploadée:', fileUrl);
      
      return fileUrl;
    } catch (error) {
      console.error('❌ Erreur lors de l\'upload de la signature:', error);
      throw error;
    }
  };

  /**
   * Fonction pour enregistrer les consommables utilisés (SANS mise à jour du stock)
   */
  const saveConsumablesUsage = async (consumables, appointmentId, technicianId) => {
    try {
      console.log('📝 Enregistrement des consommables utilisés:', consumables);
      
      if (!consumables || consumables.length === 0) {
        console.log('Aucun consommable à enregistrer');
        return { success: true, message: 'Aucun consommable à traiter', data: [] };
      }

      if (!technicianId) {
        console.warn('⚠️ ID technicien manquant');
        technicianId = user?.id || '00000000-0000-0000-0000-000000000000';
      }

      const results = {
        created: [],
        updated: [],
        errors: []
      };

      // Traiter chaque consommable un par un
      for (const item of consumables) {
        try {
          console.log(`🔍 Traitement du consommable ${item.id} (${item.name})`);
          
          const quantityToUse = item.quantity || 0;
          
          if (quantityToUse <= 0) {
            console.log(`⚠️ Quantité 0 pour ${item.name}, ignoré`);
            continue;
          }

          const now = new Date().toISOString();

          // Vérifier si une ligne existe déjà dans consumable_usage
          const { data: existingEntry, error: selectError } = await supabase
            ?.from('consumable_usage')
            ?.select('*')
            ?.eq('consumable_id', item.id)
            ?.eq('technician_id', technicianId)
            ?.eq('appointment_id', appointmentId)
            ?.maybeSingle();

          if (selectError) {
            console.error(`❌ Erreur lors de la vérification:`, selectError);
            results.errors.push({ item, error: selectError, step: 'check_usage' });
            continue;
          }

          if (existingEntry) {
            // Mettre à jour l'entrée existante
            const { data: updatedEntry, error: updateError } = await supabase
              ?.from('consumable_usage')
              ?.update({
                quantity_used: quantityToUse,
                usage_date: now,
                notes: existingEntry.notes 
                  ? `${existingEntry.notes} | Mis à jour le ${new Date(now).toLocaleString('fr-FR')}`
                  : `Utilisé lors de l'intervention ${appointmentId}`
              })
              ?.eq('id', existingEntry.id)
              ?.select()
              ?.single();

            if (updateError) throw updateError;
            
            console.log(`✅ Utilisation mise à jour pour ${item.name}`);
            results.updated.push(updatedEntry);
            
          } else {
            // Créer une nouvelle entrée
            const newEntry = {
              consumable_id: item.id,
              technician_id: technicianId,
              appointment_id: appointmentId,
              quantity_used: quantityToUse,
              usage_date: now,
              notes: `Utilisé lors de l'intervention ${appointmentId}`,
              created_at: now
            };

            const { data: createdEntry, error: insertError } = await supabase
              ?.from('consumable_usage')
              ?.insert([newEntry])
              ?.select()
              ?.single();

            if (insertError) throw insertError;
            
            console.log(`✅ Nouvelle utilisation créée pour ${item.name}`);
            results.created.push(createdEntry);
          }
          
        } catch (itemError) {
          console.error(`❌ Erreur lors du traitement du consommable ${item.id}:`, itemError);
          results.errors.push({ item, error: itemError });
        }
      }

      // Résumé final
      console.log('📊 Résumé du traitement des consommables:', {
        total: consumables.length,
        created: results.created.length,
        updated: results.updated.length,
        errors: results.errors.length
      });

      if (results.errors.length > 0) {
        console.warn('⚠️ Certains consommables n\'ont pas pu être traités:', results.errors);
      }

      return {
        success: results.errors.length === 0,
        message: `${results.created.length} créé(s), ${results.updated.length} mis à jour, ${results.errors.length} erreur(s)`,
        data: results
      };
      
    } catch (error) {
      console.error('❌ Erreur globale lors de l\'enregistrement des consommables:', error);
      throw error;
    }
  };

  /**
   * Fonction pour mettre à jour le stock des consommables (appelée uniquement quand status = completed)
   */
  const updateConsumablesStock = async (consumables, technicianId) => {
    try {
      console.log('📦 Mise à jour du stock des consommables:', consumables);
      
      if (!consumables || consumables.length === 0) {
        console.log('Aucun consommable à mettre à jour');
        return { success: true, message: 'Aucun consommable à traiter' };
      }

      if (!technicianId) {
        console.warn('⚠️ ID technicien manquant');
        technicianId = user?.id;
        if (!technicianId) {
          throw new Error('ID technicien manquant');
        }
      }

      const results = {
        updated: [],
        errors: []
      };

      // Traiter chaque consommable un par un
      for (const item of consumables) {
        try {
          console.log(`🔍 Mise à jour du stock pour ${item.id} (${item.name})`);
          
          const quantityToUse = item.quantity || 0;
          
          if (quantityToUse <= 0) {
            console.log(`⚠️ Quantité 0 pour ${item.name}, ignoré`);
            continue;
          }

          const now = new Date().toISOString();

          // Récupérer le stock actuel
          const { data: currentStock, error: stockError } = await supabase
            ?.from('consumables_techniciens')
            ?.select('quantity')
            ?.eq('consumable', item.id)
            ?.eq('technicien', technicianId)
            ?.single();

          if (stockError) {
            console.error(`❌ Erreur lors de la récupération du stock:`, stockError);
            results.errors.push({ item, error: stockError, step: 'get_stock' });
            continue;
          }

          if (!currentStock) {
            console.error(`❌ Aucun stock trouvé pour ${item.name}`);
            results.errors.push({ item, error: 'Stock not found', step: 'get_stock' });
            continue;
          }

          const newQuantity = currentStock.quantity - quantityToUse;
          
          if (newQuantity < 0) {
            console.error(`❌ Stock insuffisant pour ${item.name}: disponible ${currentStock.quantity}, demandé ${quantityToUse}`);
            results.errors.push({ 
              item, 
              error: `Stock insuffisant: ${currentStock.quantity} disponible, ${quantityToUse} demandé`,
              step: 'insufficient_stock'
            });
            continue;
          }

          // Mettre à jour le stock
          const { error: updateStockError } = await supabase
            ?.from('consumables_techniciens')
            ?.update({ 
              quantity: newQuantity,
             // updated_at: now 
            })
            ?.eq('consumable', item.id)
            ?.eq('technicien', technicianId);

          if (updateStockError) {
            console.error(`❌ Erreur lors de la mise à jour du stock:`, updateStockError);
            results.errors.push({ item, error: updateStockError, step: 'update_stock' });
            continue;
          }

          console.log(`✅ Stock mis à jour: ${currentStock.quantity} → ${newQuantity}`);
          results.updated.push({
            id: item.id,
            name: item.name,
            oldQuantity: currentStock.quantity,
            newQuantity: newQuantity,
            used: quantityToUse
          });
          
        } catch (itemError) {
          console.error(`❌ Erreur lors du traitement du consommable ${item.id}:`, itemError);
          results.errors.push({ item, error: itemError });
        }
      }

      // Résumé final
      console.log('📊 Résumé de la mise à jour des stocks:', {
        total: consumables.length,
        updated: results.updated.length,
        errors: results.errors.length
      });

      if (results.errors.length > 0) {
        console.warn('⚠️ Certains stocks n\'ont pas pu être mis à jour:', results.errors);
        
        const errorMessages = results.errors.map(e => 
          `${e.item.name}: ${e.error.message || e.error}`
        ).join('\n');
        
        alert(`Attention: ${results.errors.length} stock(s) n'ont pas pu être mis à jour:\n${errorMessages}`);
      }

      return {
        success: results.errors.length === 0,
        message: `${results.updated.length} mis à jour, ${results.errors.length} erreur(s)`,
        data: results
      };
      
    } catch (error) {
      console.error('❌ Erreur globale lors de la mise à jour des stocks:', error);
      throw error;
    }
  };

  /**
   * Traite les photos avant sauvegarde
   */
  const processPhotos = async (photos) => {
    if (!photos) {
      return { before: [], after: [] };
    }

    console.log('🖼️ Traitement des photos:', photos);
    
    const processedPhotos = {
      before: [],
      after: []
    };

    // Traiter les photos "avant"
    if (photos.before && Array.isArray(photos.before)) {
      for (const photo of photos.before) {
        try {
          // Ignorer les valeurs null/undefined
          if (!photo) continue;
          
          // Si c'est déjà une URL Bunny, la garder
          if (isBunnyUrl(photo)) {
            processedPhotos.before.push(photo);
            continue;
          }
          
          // Uploader le fichier
          const url = await uploadFileToBunny(photo, 'before');
          processedPhotos.before.push(url);
        } catch (error) {
          console.error('❌ Erreur traitement photo avant:', error);
          // En cas d'erreur, garder la valeur originale si c'est une string
          if (typeof photo === 'string') {
            processedPhotos.before.push(photo);
          }
        }
      }
    }

    // Traiter les photos "après"
    if (photos.after && Array.isArray(photos.after)) {
      for (const photo of photos.after) {
        try {
          if (!photo) continue;
          
          if (isBunnyUrl(photo)) {
            processedPhotos.after.push(photo);
            continue;
          }
          
          const url = await uploadFileToBunny(photo, 'after');
          processedPhotos.after.push(url);
        } catch (error) {
          console.error('❌ Erreur traitement photo après:', error);
          if (typeof photo === 'string') {
            processedPhotos.after.push(photo);
          }
        }
      }
    }

    console.log('✅ Photos traitées:', processedPhotos);
    return processedPhotos;
  };

  const handleSave = async (interventionData) => {
    setIsSaving(true);
    setUploading(true);
    
    try {
      console.log('💾 Sauvegarde des données:', interventionData);
      
      let processedData = { ...interventionData };
      
      // Traiter les photos si elles existent
      if (interventionData.photos) {
        try {
          const processedPhotos = await processPhotos(interventionData.photos);
          processedData.photos = processedPhotos;
        } catch (error) {
          console.error('❌ Erreur lors du traitement des photos:', error);
          processedData.photos = interventionData.photos;
        }
      }
      
      // Traiter la signature si elle existe et est une dataURL
      if (interventionData.signature && 
          typeof interventionData.signature === 'string' && 
          interventionData.signature.startsWith('data:image')) {
        try {
          const uploadedSignature = await uploadSignatureToBunny(interventionData.signature);
          processedData.signature = uploadedSignature;
        } catch (error) {
          console.error('❌ Erreur lors de l\'upload de la signature:', error);
          processedData.signature = interventionData.signature;
        }
      }

      // Récupérer l'ID du technicien
      const technicianId = user?.id || null;
      console.log('👨‍🔧 ID du technicien:', technicianId);

      // Enregistrer les consommables utilisés (SANS mise à jour du stock)
      if (interventionData.consumables && interventionData.consumables.length > 0) {
        try {
          const result = await saveConsumablesUsage(
            interventionData.consumables, 
            id, 
            technicianId
          );
          
          console.log('📊 Résultat de l\'enregistrement des consommables:', result);
          
        } catch (error) {
          console.error('❌ Erreur lors de l\'enregistrement des consommables:', error);
        }
      }

      // Préparer les données pour l'API appointment
      const appointmentResult = [{
        image: [processedData.photos],
        signature: processedData.signature,
        checklist: processedData.checklist
      }];
      
      // Mettre à jour l'API avec les données sauvegardées
      if (appointmentService.updateAppointment) {
        await appointmentService.updateAppointment(id, {
          notes: processedData.notes,
          appointment_result: appointmentResult,
          updated_at: new Date().toISOString(),
          status: 'on_site'
        });
      }
      
      // Mettre à jour l'état local
      setActiveAppointment(prev => ({
        ...prev,
        ...processedData,
        status: 'in_progress'
      }));
      
      console.log('✅ Intervention sauvegardée avec succès');
      
    } catch (err) {
      console.error('❌ Erreur lors de la sauvegarde:', err);
      alert('Erreur lors de la sauvegarde. Veuillez réessayer.');
      throw err;
    } finally {
      setIsSaving(false);
      setUploading(false);
    }
  };

  const handleConfirmCompletion = async (completionData) => {
    setIsCompleting(true);
    setUploading(true);
    
    try {
      console.log('🏁 Finalisation de l\'intervention:', completionData);
      
      let processedData = { ...completionData };
      
      // 1. Traiter les photos si elles existent
      if (completionData.photos) {
        try {
          const processedPhotos = await processPhotos(completionData.photos);
          processedData.photos = processedPhotos;
        } catch (error) {
          console.error('❌ Erreur lors du traitement des photos:', error);
          processedData.photos = completionData.photos;
        }
      }
      
      // 2. Traiter la signature si elle existe (dataURL)
      if (completionData.signature && 
          typeof completionData.signature === 'string' && 
          completionData.signature.startsWith('data:image')) {
        try {
          const uploadedSignature = await uploadSignatureToBunny(completionData.signature);
          processedData.signature = uploadedSignature;
          console.log('✅ Signature uploadée vers Bunny.net:', uploadedSignature);
        } catch (error) {
          console.error('❌ Erreur lors de l\'upload de la signature:', error);
          processedData.signature = completionData.signature;
        }
      }
      
      // 3. Récupérer l'ID du technicien
      const technicianId = user?.id || null;
      
      // 4. Enregistrer les consommables utilisés (SANS mise à jour du stock)
      if (completionData.consumables && completionData.consumables.length > 0) {
        try {
          const result = await saveConsumablesUsage(
            completionData.consumables, 
            id, 
            technicianId
          );
          console.log('📊 Résultat de l\'enregistrement des consommables:', result);
        } catch (error) {
          console.error('❌ Erreur lors de l\'enregistrement des consommables:', error);
        }
      }
      
      // 5. Préparer les données pour l'API appointment
      const appointmentResult = [{
        image: [processedData.photos],
        signature: processedData.signature,
        checklist: processedData.checklist
      }];
      
      // 6. Mettre à jour l'API avec le statut terminé
      if (appointmentService.updateAppointment) {
        await appointmentService.updateAppointment(id, {
          status: 'completed',
          appointment_result: appointmentResult,
          notes: processedData.notes
        });
      }

      // 7. MAINTENANT QUE LE STATUT EST COMPLETED, METTRE À JOUR LE STOCK
      if (completionData.consumables && completionData.consumables.length > 0) {
        try {
          const stockResult = await updateConsumablesStock(
            completionData.consumables, 
            technicianId
          );
          console.log('📊 Résultat de la mise à jour des stocks:', stockResult);
          
          if (!stockResult.success) {
            alert('Attention: Certains stocks n\'ont pas pu être mis à jour correctement.');
          }
        } catch (error) {
          console.error('❌ Erreur lors de la mise à jour des stocks:', error);
          alert('Erreur lors de la mise à jour des stocks. Veuillez contacter le support.');
        }
      }

      // Mettre à jour l'état local
      setActiveAppointment(prev => ({
        ...prev,
        status: 'completed',
        completedAt: processedData.completedAt,
        photos: processedData.photos,
        signature: processedData.signature,
        notes: processedData.notes,
        checklist: processedData.checklist,
        consumables: processedData.consumables
      }));
      
      setShowCompletionModal(false);
      
      console.log('✅ Intervention terminée avec succès');
      
      // Afficher un message de succès
     // alert('Intervention terminée avec succès !');
      
      // Rediriger vers la liste des interventions après un court délai
      setTimeout(() => {
        navigate('/technician-daily-routes');
      }, 2000);
      
    } catch (err) {
      console.error('❌ Erreur lors de la complétion:', err);
      alert('Erreur lors de la finalisation. Veuillez réessayer.');
    } finally {
      setIsCompleting(false);
      setUploading(false);
    }
  };

  /**
   * Fonction pour supprimer un consommable de la table consumables_usage
   */
  const deleteConsumableUsage = async (consumableId, appointmentId, technicianId) => {
    try {
      console.log(`🗑️ Suppression du consommable ${consumableId} pour l'intervention ${appointmentId}`);
      
      if (!consumableId || !appointmentId) {
        console.error('❌ ID consommable ou ID intervention manquant');
        return { success: false, message: 'Paramètres manquants' };
      }

      // 1. Vérifier si la ligne existe
      const { data: existingEntries, error: selectError } = await supabase
        ?.from('consumable_usage')
        ?.select('*')
        ?.eq('consumable_id', consumableId)
        ?.eq('appointment_id', appointmentId);

      if (selectError) {
        console.error('❌ Erreur lors de la vérification:', selectError);
        throw selectError;
      }

      if (!existingEntries || existingEntries.length === 0) {
        console.log('Aucune entrée trouvée à supprimer');
        return { success: true, message: 'Aucune entrée à supprimer' };
      }

      // 2. Supprimer la ligne
      const { error: deleteError } = await supabase
        ?.from('consumable_usage')
        ?.delete()
        ?.eq('consumable_id', consumableId)
        ?.eq('appointment_id', appointmentId);

      if (deleteError) {
        console.error('❌ Erreur lors de la suppression:', deleteError);
        throw deleteError;
      }

      console.log(`✅ Consommable ${consumableId} supprimé avec succès`);
      return { 
        success: true, 
        message: 'Consommable supprimé',
        deletedCount: existingEntries.length 
      };
      
    } catch (error) {
      console.error('❌ Erreur lors de la suppression du consommable:', error);
      throw error;
    }
  };

  const handleDeleteConsumable = async (consumableId) => {
    try {
      console.log(`🗑️ Suppression du consommable ${consumableId}`);
      
      const technicianId = user?.id || null;
      
      const result = await deleteConsumableUsage(consumableId, id, technicianId);
      
      if (result.success) {
        console.log('✅ Suppression réussie:', result.message);
        
        // Mettre à jour l'état local
        setActiveAppointment(prev => ({
          ...prev,
          consumables: prev.consumables.filter(item => item.id !== consumableId)
        }));
        
        return result;
      }
    } catch (error) {
      console.error('❌ Erreur lors de la suppression:', error);
      throw error;
    }
  };

  const handleUpdatePhotos = (photos) => {
    setActiveAppointment((prev) => ({
      ...prev,
      photos: photos
    }));
  };

  const handleUpdateSignature = (signature) => {
    setActiveAppointment((prev) => ({
      ...prev,
      signature: signature
    }));
  };

  const handleLogout = () => {
    navigate('/');
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleComplete = async (completionData) => {
    setShowCompletionModal(true);
  };

  // Overlay de chargement pendant l'upload
  const renderUploadingOverlay = () => {
    if (!uploading) return null;
    return null;
  };

  const CompletionModal = ({ appointment, onClose, onConfirm }) => {
    const [loading, setLoading] = useState(false);

    const handleConfirm = async () => {
      setLoading(true);
      try {
        const completionData = {
          checklist: appointment.checklist || [],
          photos: appointment.photos || { before: [], after: [] },
          signature: appointment.signature || null,
          notes: appointment.notes || '',
          consumables: appointment.consumables || [],
          completedAt: new Date().toISOString()
        };
        
        await onConfirm(completionData);
      } catch (error) {
        console.error('❌ Erreur lors de la confirmation:', error);
      } finally {
        setLoading(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Confirmer la fin de l'intervention
            </h3>
            <p className="text-gray-600 mb-6">
              Vous êtes sur le point de terminer l'intervention pour {appointment.customerName}. 
              Cette action ne peut pas être annulée.
            </p>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded p-4 mb-6">
              <p className="text-sm text-yellow-800">
                <strong>Vérifications requises :</strong>
              </p>
              <ul className="text-sm text-yellow-800 mt-2 list-disc pl-5">
                <li>Checklist complétée</li>
                <li>Photos avant/après ajoutées</li>
                <li>Signature du client obtenue</li>
              </ul>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={onClose}
                disabled={loading}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={handleConfirm}
                disabled={loading}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark disabled:opacity-50"
              >
                {loading ? 'Traitement...' : 'Confirmer'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-foreground">Chargement des détails de l'intervention...</p>
          <p className="text-sm text-muted-foreground mt-2">Veuillez patienter</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <RoleBasedHeader
          userRole="technician"
          userName="Marc Lefebvre"
          onLogout={handleLogout}
        />
        <div className="main-content container mx-auto px-4 py-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Erreur</h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{error}</p>
                </div>
                <div className="mt-3 space-x-3">
                  <button
                    onClick={handleBack}
                    className="text-sm font-medium text-primary hover:text-primary-dark"
                  >
                    ← Retour
                  </button>
                  <button
                    onClick={() => window.location.reload()}
                    className="text-sm font-medium text-red-800 hover:text-red-900"
                  >
                    Réessayer
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {renderUploadingOverlay()}
      
      <RoleBasedHeader
        userRole="technician"
        userName="Marc Lefebvre"
        onLogout={handleLogout}
      />

      <main className="main-content">
        <div className="container mx-auto px-4 py-6">
          <div className="mb-4">
            <button
              onClick={handleBack}
              className="flex items-center text-primary hover:text-primary-dark mb-4"
            >
              <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Retour au planning
            </button>
            <h1 className="text-2xl font-bold text-foreground">
              Intervention #{activeAppointment?.reference} - {activeAppointment?.serviceType}
            </h1>
            <p className="text-muted-foreground mt-1">
              Client: {activeAppointment?.customerName}
            </p>
            {activeAppointment?.status === 'completed' && (
              <div className="mt-2 p-2 bg-success/10 border border-success/20 rounded text-sm text-success">
                <div className="flex items-center">
                  <span className="mr-2">✓</span>
                  Intervention terminée le {new Date(activeAppointment.completedAt).toLocaleDateString('fr-FR')}
                </div>
              </div>
            )}
          </div>
          
          <div className="flex items-center justify-center min-h-[calc(100vh-180px)]">
            <div className="h-full w-full">
              {activeAppointment ? (
                <InterventionDetails
                  key={`${activeAppointment.id}-${checklistLoaded}`}
                  appointment={activeAppointment}
                  onComplete={handleConfirmCompletion}
                  onSave={handleSave}
                  onUpdatePhotos={handleUpdatePhotos}
                  onUpdateSignature={handleUpdateSignature}
                  onUpdateConsumables={handleUpdateConsumables}
                  onDeleteConsumable={handleDeleteConsumable}
                  isSaving={isSaving}
                  isCompleting={isCompleting}
                  uploading={uploading}
                  user={user}
                />
              ) : (
                <div className="bg-card rounded-lg shadow-sm h-full flex items-center justify-center">
                  <div className="text-center p-8">
                    <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                      <svg className="w-10 h-10 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-foreground mb-2">
                      Intervention non trouvée
                    </h3>
                    <p className="text-muted-foreground max-w-sm mx-auto mb-4">
                      L'intervention demandée n'existe pas ou a été supprimée.
                    </p>
                    <button
                      onClick={handleBack}
                      className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
                    >
                      Retour au planning
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {showCompletionModal && activeAppointment && (
        <CompletionModal
          appointment={activeAppointment}
          onClose={() => setShowCompletionModal(false)}
          onConfirm={handleConfirmCompletion}
        />
      )}
    </div>
  );
};

export default InterventionPage;